import React, { useState, useEffect } from 'react';
import './App.css';

// Mock API function - simulates fetching data from server
const mockAPIFetch = () => {
  return new Promise((resolve) => {
    // Show loading for 1.5 seconds to simulate network request
    setTimeout(() => {
      const mockTransactions = [
        { id: 1, date: '2025-03-15', description: 'Salary', category: 'Salary', type: 'income', amount: 4200 },
        { id: 2, date: '2025-03-10', description: 'Grocery shopping', category: 'Food', type: 'expense', amount: 185 },
        { id: 3, date: '2025-03-05', description: 'Uber ride', category: 'Transport', type: 'expense', amount: 24 },
        { id: 4, date: '2025-02-28', description: 'Freelance', category: 'Freelance', type: 'income', amount: 1200 },
        { id: 5, date: '2025-02-20', description: 'Electricity bill', category: 'Bills', type: 'expense', amount: 95 },
        { id: 6, date: '2025-02-14', description: 'Headphones', category: 'Shopping', type: 'expense', amount: 210 },
      ];
      resolve(mockTransactions);
    }, 1500);
  });
};

function App() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState('viewer');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Advanced filters - Date Range & Amount Range
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');

  // Load data from mock API on component mount
  useEffect(() => {
    loadTransactionsFromAPI();
  }, []);

  const loadTransactionsFromAPI = async () => {
    setLoading(true);
    const data = await mockAPIFetch();
    setTransactions(data);
    setLoading(false);
  };

  // Dark mode effect
  // Dark mode effect - FIXED VERSION
  useEffect(() => {
    const savedDark = localStorage.getItem('darkMode') === 'true';
    if (savedDark) document.body.classList.add('dark');
  }, []);

  // Dark mode toggle function - separate function for clarity
  const toggleDarkMode = () => {
    document.body.classList.toggle('dark');
    const isDark = document.body.classList.contains('dark');
    localStorage.setItem('darkMode', isDark);
  };
  // Add transaction function
  const addTransaction = () => {
    const desc = prompt('Enter description:', 'Lunch');
    if (!desc) return;
    
    const amountInput = prompt('Enter amount:', '50');
    if (!amountInput) return;
    const amount = parseFloat(amountInput);
    if (isNaN(amount)) {
      alert('Please enter a valid number');
      return;
    }
    
    const type = prompt('Type (income or expense):', 'expense');
    if (type !== 'income' && type !== 'expense') {
      alert('Type must be "income" or "expense"');
      return;
    }
    
    const category = prompt('Category (Food, Shopping, Transport, Bills, Salary, Freelance):', 'Food');
    if (!category) return;
    
    const date = prompt('Date (YYYY-MM-DD):', new Date().toISOString().slice(0,10));
    if (!date) return;
    
    const newId = Math.max(...transactions.map(t => t.id), 0) + 1;
    const newTransaction = {
      id: newId,
      date: date,
      description: desc,
      category: category,
      type: type,
      amount: Math.abs(amount)
    };
    
    setTransactions([newTransaction, ...transactions]);
  };

  const deleteTransaction = (id) => {
    if (window.confirm('Delete this transaction?')) {
      setTransactions(transactions.filter(t => t.id !== id));
    }
  };

  const editTransaction = (tx) => {
    const newDesc = prompt('Edit description:', tx.description);
    if (newDesc && newDesc.trim()) {
      setTransactions(transactions.map(t => 
        t.id === tx.id ? { ...t, description: newDesc } : t
      ));
    }
  };

  // CSV Export Function
  const exportToCSV = () => {
    // Get filtered transactions for export
    let exportData = [...filteredTransactions];
    
    // Apply date range to export
    if (startDate) {
      exportData = exportData.filter(t => t.date >= startDate);
    }
    if (endDate) {
      exportData = exportData.filter(t => t.date <= endDate);
    }
    
    if (exportData.length === 0) {
      alert('No transactions to export!');
      return;
    }
    
    // Convert to CSV format
    const headers = ['Date', 'Description', 'Category', 'Type', 'Amount'];
    const csvRows = [];
    csvRows.push(headers.join(','));
    
    for (const transaction of exportData) {
      const values = [
        transaction.date,
        `"${transaction.description}"`,
        transaction.category,
        transaction.type,
        transaction.amount
      ];
      csvRows.push(values.join(','));
    }
    
    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${new Date().toISOString().slice(0,19)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    alert(`Exported ${exportData.length} transactions to CSV!`);
  };

  // Calculate totals
  const totalIncome = transactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
  const totalExpense = transactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0);
  const balance = totalIncome - totalExpense;

  // Advanced Filtering Function
  const filteredTransactions = transactions.filter(t => {
    const matchesSearch = t.description.toLowerCase().includes(search.toLowerCase()) || 
                          t.category.toLowerCase().includes(search.toLowerCase());
    const matchesType = typeFilter === 'all' || t.type === typeFilter;
    const matchesCategory = categoryFilter === 'all' || t.category === categoryFilter;
    
    // Date range filter
    let matchesDate = true;
    if (startDate && t.date < startDate) matchesDate = false;
    if (endDate && t.date > endDate) matchesDate = false;
    
    // Amount range filter
    let matchesAmount = true;
    if (minAmount && t.amount < parseFloat(minAmount)) matchesAmount = false;
    if (maxAmount && t.amount > parseFloat(maxAmount)) matchesAmount = false;
    
    return matchesSearch && matchesType && matchesCategory && matchesDate && matchesAmount;
  });

  // Sort by date (newest first)
  const sortedTransactions = [...filteredTransactions].sort((a, b) => new Date(b.date) - new Date(a.date));
  
  // Get unique categories
  const categories = [...new Set(transactions.map(t => t.category))];

  // Category Chart Data
  const expenses = transactions.filter(t => t.type === 'expense');
  const categoryTotals = {};
  expenses.forEach(exp => { 
    categoryTotals[exp.category] = (categoryTotals[exp.category] || 0) + exp.amount; 
  });
  
  // Trend Chart Data
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  const monthlyNet = months.map((_, idx) => {
    let net = 0;
    transactions.forEach(tx => {
      const txMonth = new Date(tx.date).getMonth();
      if (txMonth === idx) net += tx.type === 'income' ? tx.amount : -tx.amount;
    });
    return net;
  });
  const maxTrend = Math.max(...monthlyNet, 1);

  // Insights
  let highestCategory = 'None';
  let highestAmount = 0;
  Object.entries(categoryTotals).forEach(([cat, amt]) => { 
    if (amt > highestAmount) { 
      highestAmount = amt; 
      highestCategory = cat; 
    } 
  });
  
  const currentMonth = new Date().getMonth();
  const currentMonthExpense = expenses.filter(e => new Date(e.date).getMonth() === currentMonth)
    .reduce((sum, e) => sum + e.amount, 0);
  const lastMonthExpense = expenses.filter(e => new Date(e.date).getMonth() === currentMonth - 1)
    .reduce((sum, e) => sum + e.amount, 0);

  const colors = ['#3b82f6', '#f97316', '#10b981', '#8b5cf6', '#ec489a', '#06b6d4'];

  // Clear all advanced filters
  const clearAdvancedFilters = () => {
    setStartDate('');
    setEndDate('');
    setMinAmount('');
    setMaxAmount('');
    setSearch('');
    setTypeFilter('all');
    setCategoryFilter('all');
  };

  // Loading State
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <h2>Loading financial data from API...</h2>
        <p>Fetching your transactions</p>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="header">
        <h1>💰 Finance Dashboard</h1>
        <div className="header-controls">
          <select value={role} onChange={(e) => setRole(e.target.value)} className="role-select">
            <option value="viewer">👁️ Viewer Mode</option>
            <option value="admin">🛠️ Admin Mode</option>
          </select>
         <button onClick={toggleDarkMode} className="dark-btn">🌙 Dark Mode</button> <button onClick={exportToCSV} className="export-btn">📥 Export CSV</button>
        </div>
      </div>

      {/* Summary Cards with Animation */}
      <div className="summary-grid">
        <div className="card fade-in">
          <h3>Total Balance</h3>
          <div className={`amount ${balance >= 0 ? 'positive' : 'negative'} slide-in`}>
            ${balance.toLocaleString()}
          </div>
        </div>
        <div className="card fade-in">
          <h3>Income</h3>
          <div className="amount positive slide-in">+${totalIncome.toLocaleString()}</div>
        </div>
        <div className="card fade-in">
          <h3>Expenses</h3>
          <div className="amount negative slide-in">-${totalExpense.toLocaleString()}</div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        <div className="chart-card fade-in">
          <h3>📊 Spending by Category</h3>
          <div className="simple-chart">
            {Object.entries(categoryTotals).map(([cat, amt], i) => (
              <div key={cat} className="chart-bar-container">
                <div className="chart-label">{cat}</div>
                <div className="chart-bar-wrapper">
                  <div 
                    className="chart-bar animated-bar" 
                    style={{ 
                      width: `${(amt / Math.max(...Object.values(categoryTotals), 1)) * 100}%`, 
                      backgroundColor: colors[i % colors.length] 
                    }} 
                  />
                  <span className="chart-value">${amt}</span>
                </div>
              </div>
            ))}
            {Object.keys(categoryTotals).length === 0 && <div>No expense data</div>}
          </div>
        </div>
        <div className="chart-card fade-in">
          <h3>📈 Monthly Trend</h3>
          <div className="trend-chart">
            <div className="trend-bars">
              {monthlyNet.map((value, i) => (
                <div key={i} className="trend-bar-container">
                  <div 
                    className="trend-bar animated-bar" 
                    style={{ 
                      height: `${Math.abs(value / maxTrend) * 150}px`, 
                      backgroundColor: value >= 0 ? '#10b981' : '#ef4444' 
                    }} 
                  />
                  <div className="trend-label">{months[i]}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Insights Section */}
      <div className="insights-section fade-in">
        <h3>💡 Smart Insights</h3>
        <div className="insights-grid">
          <div className="insight-card">
            🏆 Highest Spending: <strong>{highestCategory}</strong> (${highestAmount})
          </div>
          <div className="insight-card">
            📊 Monthly Change: {lastMonthExpense > 0 ? 
              `${((currentMonthExpense - lastMonthExpense) / lastMonthExpense * 100).toFixed(0)}% vs last month` : 
              'Add more data'}
          </div>
          <div className="insight-card">
            💰 Total Transactions: {transactions.length}
          </div>
        </div>
      </div>

      {/* Advanced Filters Section */}
      <div className="advanced-filters slide-in">
        <h3>🔍 Advanced Filters</h3>
        <div className="filter-row">
          <div className="filter-group">
            <label>📅 Date Range:</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} placeholder="Start Date" />
            <span>to</span>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} placeholder="End Date" />
          </div>
          <div className="filter-group">
            <label>💰 Amount Range:</label>
            <input type="number" placeholder="Min $" value={minAmount} onChange={(e) => setMinAmount(e.target.value)} />
            <span>to</span>
            <input type="number" placeholder="Max $" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} />
          </div>
          <button onClick={clearAdvancedFilters} className="clear-filters-btn">🗑️ Clear All Filters</button>
        </div>
      </div>

      {/* Basic Filters */}
      <div className="filters">
        <input 
          type="text" 
          placeholder="🔍 Search transactions..." 
          value={search}
          onChange={(e) => setSearch(e.target.value)} 
          className="search-input" 
        />
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="all">All Types</option>
          <option value="income">Income Only</option>
          <option value="expense">Expense Only</option>
        </select>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="all">All Categories</option>
          {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
        </select>
        {role === 'admin' && (
          <button onClick={addTransaction} className="add-btn">
            + Add Transaction
          </button>
        )}
      </div>

      {/* Transactions Table */}
      <div className="transactions-table">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Category</th>
              <th>Type</th>
              <th>Amount</th>
              {role === 'admin' && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {sortedTransactions.map(tx => (
              <tr key={tx.id} className="table-row fade-in">
                <td>{tx.date}</td>
                <td>{tx.description}</td>
                <td>{tx.category}</td>
                <td>
                  <span className={`badge ${tx.type}`}>
                    {tx.type}
                  </span>
                </td>
                <td className={tx.type === 'income' ? 'income-amount' : 'expense-amount'}>
                  {tx.type === 'income' ? '+' : '-'}${tx.amount}
                </td>
                {role === 'admin' && (
                  <td className="action-buttons">
                    <button onClick={() => editTransaction(tx)} className="edit-btn">✏️</button>
                    <button onClick={() => deleteTransaction(tx.id)} className="delete-btn">🗑️</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {sortedTransactions.length === 0 && (
          <div className="empty-state">No transactions found matching filters</div>
        )}
      </div>
      
      <div className="footer-note">
        <p>✨ {filteredTransactions.length} transactions shown | Mock API loaded | Export to CSV available</p>
      </div>
    </div>
  );
}

export default App;